import React, { createContext, useContext, useState } from "react";
import {
  FilterFields,
  TabTypes,
  CreateSwapRequestContextType,
  CreateSwapRequestProviderProps,
  ExactPayFields,
  ExactGetFields,
  ExactPaySlippageTypes,
  BaseSwapFields,
  AmountSnapshot,
  AddressBookOrder,
} from "../types";
import { EXACT_PAY_MAX_SLIPPAGE_MAP } from "../constants/slippage";
import { ISwapPathInfo } from "../../../../api/swap";

const CreateSwapRequestContext = createContext<
  CreateSwapRequestContextType | undefined
>(undefined);

export const useCreateSwapRequestContext = () => {
  const context = useContext(CreateSwapRequestContext);
  if (context === undefined) {
    throw new Error(
      "useCreateSwapRequestContext must be used within a CreateSwapRequestProvider"
    );
  }
  return context;
};

// Helper function to create base fields
const createBaseSwapFields = (): BaseSwapFields => ({
  walletAddress: "",
  balance: "",
  amountSnapShot: { exactAmount: null, maxSlippage: null } as AmountSnapshot,
  addressBookOrder: {
    to_address: "",
    amount: "",
    to_wallet_tag: "",
    wallet_type: "",
    display_name: "",
  } as AddressBookOrder,
  estimateAmount: "",
  exchangeRate: "",
  quotedTime: "",
  rawWalletInfo: null,
});

export const initFilterFields: FilterFields = {
  chainName: "",
  currencyPair: "",
  fromCurrency: "",
  toCurrency: "",
  maxSlippage: String(
    EXACT_PAY_MAX_SLIPPAGE_MAP[ExactPaySlippageTypes.MAX_SLIPPAGE_0_1]
  ),
  isTronRent: false,
};

export const initExactPayFields: ExactPayFields = {
  ...createBaseSwapFields(),
  exactPayAmount: "",
  getMin: "",
};

export const initExactGetFields: ExactGetFields = {
  ...createBaseSwapFields(),
  exactGetAmount: "",
  payMax: "",
};

export const CreateSwapRequestProvider: React.FC<
  CreateSwapRequestProviderProps
> = ({ children }) => {
  const [filterFields, setFilterFields] =
    useState<FilterFields>(initFilterFields);

  // for tab picker
  const [activeTabId, setActiveTabId] = useState<TabTypes>(TabTypes.EXACT_PAY);
  const [maxSlippageTabId, setMaxSlippageTabId] =
    useState<ExactPaySlippageTypes>(ExactPaySlippageTypes.MAX_SLIPPAGE_0_1);

  const [exactPayFields, setExactPayFields] =
    useState<ExactPayFields>(initExactPayFields);

  const [exactGetFields, setExactGetFields] =
    useState<ExactGetFields>(initExactGetFields);

  const [swapPaths, setSwapPaths] = useState<ISwapPathInfo[]>([]);

  const resetEverything = (isResetActiveTab?: boolean) => {
    setFilterFields(initFilterFields);
    isResetActiveTab && setActiveTabId(TabTypes.EXACT_PAY);
    setMaxSlippageTabId(ExactPaySlippageTypes.MAX_SLIPPAGE_0_1);
    setExactPayFields(initExactPayFields);
    setExactGetFields(initExactGetFields);
  };

  const resetContent = () => {
    setExactPayFields(initExactPayFields);
    setExactGetFields(initExactGetFields);
    setMaxSlippageTabId(ExactPaySlippageTypes.MAX_SLIPPAGE_0_1);
  };

  const resetQuoteDetails = () => {
    setExactPayFields((prev) => ({
      ...prev,
      exactPayAmount: "",
      estimateAmount: "",
      exchangeRate: "",
      getMin: "",
      quotedTime: "",
    }));
    setExactGetFields((prev) => ({
      ...prev,
      exactGetAmount: "",
      estimateAmount: "",
      exchangeRate: "",
      payMax: "",
      quotedTime: "",
    }));
  };

  const value: CreateSwapRequestContextType = {
    // filter state
    filterFields,
    setFilterFields,

    // tab picker state
    activeTabId,
    setActiveTabId,
    maxSlippageTabId,
    setMaxSlippageTabId,

    // data state
    exactPayFields,
    setExactPayFields,
    exactGetFields,
    setExactGetFields,
    swapPaths,
    setSwapPaths,

    // helper functions
    resetEverything,
    resetContent,
    resetQuoteDetails,
  };

  return (
    <CreateSwapRequestContext.Provider value={value}>
      {children}
    </CreateSwapRequestContext.Provider>
  );
};
